/**
 * Converts an object with decimal values summing to 100 into an object with integer values summing to 100.
 *
 * The conversion ensures:
 * - The sum of all values in the output object is exactly 100.
 * - The smallest possible rounding error per key.
 * - Preference is given to keys with the highest decimal fractions during rounding.
 *
 * Example input:
 * ```js
 * {
 *   a: 50.943396226415096,
 *   b: 0,
 *   c: 41.509433962264154,
 *   d: 7.547169811320755
 * }
 * ```
 *
 * Example output:
 * ```js
 * {
 *   a: 51,
 *   b: 0,
 *   c: 41,
 *   d: 8
 * }
 * ```
 *
 * @param {Object.<string, number>} input - An object with numeric values that sum to 100 (decimals allowed).
 * @returns {Object.<string, number>} An object with integer values that sum to 100.
 */
export function roundPercentObjectValues(input) {
  const totalFraction = Object.values(input).reduce((sum, value) => sum + value, 0);
  if (Math.round(totalFraction) !== 100) {
    return Object.fromEntries(Object.entries(input).map(([key, value]) => [key, Math.round(value)]));
  }

  const entries = Object.entries(input);

  const floored = entries.map(([key, value]) => ({
    key,
    intValue: Math.floor(value),
    fraction: value - Math.floor(value),
  }));

  const total = floored.reduce((sum, item) => sum + item.intValue, 0);

  const remainder = 100 - total;

  floored.sort((a, b) => b.fraction - a.fraction);

  for (let i = 0; i < remainder; i++) {
    floored[i].intValue += 1;
  }

  const result = {};
  for (const item of floored) {
    result[item.key] = item.intValue;
  }

  return result;
}
